/**
 * /tools/values-card-sort?clientId=X — STP-026 + STP-134.
 *
 * Step 2 60-card sort. Round 1: sort each card into one of two piles —
 * `star` (this is deeply me) or `check` (keep, lighter). Round 2:
 * top-5 ranking from the starred pile. (#21/#22, 2026-06-12: two buckets,
 * moved to Step 2.)
 *
 * Click-to-bucket implementation (drag-and-drop deferred to R1.x). The
 * 60 values come from `@h3eromap/shared`'s CFG-129 seed, locale-aware
 * via `loadValuesPool`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { loadValuesPool, type SeedLocale } from '@h3eromap/shared';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

// #21 — two buckets only: star (deeply me) + check (keep, lighter).
const BUCKETS = ['star', 'check'] as const;
type Bucket = (typeof BUCKETS)[number];

type Buckets = Record<string, Bucket | null>;

interface FormState {
  buckets: Buckets;
  topFive: string[];
  reflection: string;
}

const DEFAULT_FORM: FormState = { buckets: {}, topFive: [], reflection: '' };

export function ValuesCardSortPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const locale: SeedLocale = i18n.language === 'de' ? 'de' : 'en';
  const valuesPool = useMemo(() => loadValuesPool(locale), [locale]);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'values-card-sort');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM, buckets: {} };
          if (Array.isArray(p.round1)) {
            for (const e of p.round1) {
              if (typeof e === 'object' && e !== null) {
                const entry = e as Record<string, unknown>;
                if (
                  typeof entry.valueName === 'string' &&
                  typeof entry.bucket === 'string' &&
                  (BUCKETS as readonly string[]).includes(entry.bucket)
                ) {
                  next.buckets[entry.valueName] = entry.bucket as Bucket;
                }
              }
            }
          }
          if (Array.isArray(p.topFive)) {
            next.topFive = p.topFive.filter((x): x is string => typeof x === 'string').slice(0, 5);
          }
          if (typeof p.reflection === 'string') {
            next.reflection = p.reflection;
          }
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  // #21 — only starred cards are eligible for the Round-2 top-5.
  const starred = useMemo(
    () => valuesPool.filter((v) => form.buckets[v] === 'star'),
    [valuesPool, form.buckets],
  );

  const canSubmit = useMemo(
    () => !submitting && hydrated && form.topFive.length >= 1 && form.topFive.length <= 5,
    [submitting, hydrated, form.topFive],
  );

  const setBucket = (valueName: string, bucket: Bucket): void => {
    setForm((prev) => {
      const next = { ...prev.buckets, [valueName]: bucket };
      const stillEligible = next[valueName] === 'star';
      const nextTopFive = stillEligible
        ? prev.topFive
        : prev.topFive.filter((v) => v !== valueName);
      return { ...prev, buckets: next, topFive: nextTopFive };
    });
  };

  const toggleTopFive = (valueName: string): void => {
    setForm((prev) => {
      if (prev.topFive.includes(valueName)) {
        return { ...prev, topFive: prev.topFive.filter((v) => v !== valueName) };
      }
      if (prev.topFive.length >= 5) return prev;
      return { ...prev, topFive: [...prev.topFive, valueName] };
    });
  };

  const moveTopFive = (index: number, direction: -1 | 1): void => {
    setForm((prev) => {
      const next = [...prev.topFive];
      const target = index + direction;
      const a = next[index];
      const b = next[target];
      if (a === undefined || b === undefined) return prev;
      next[index] = b;
      next[target] = a;
      return { ...prev, topFive: next };
    });
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.valuesCardSort.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.valuesCardSort.errors.missingClient'));
      return;
    }
    if (form.topFive.length === 0) {
      setSubmitError(t('tools.valuesCardSort.errors.needsTopFive'));
      return;
    }
    setSubmitting(true);
    const round1 = Object.entries(form.buckets)
      .filter((entry): entry is [string, Bucket] => entry[1] !== null)
      .map(([valueName, bucket]) => ({ valueName, bucket }));
    const payload = {
      round1,
      topFive: form.topFive,
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'values-card-sort',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.valuesCardSort.errors.generic'));
      } else {
        setSubmitError(t('tools.valuesCardSort.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="values-card-sort-missing-client">
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.valuesCardSort.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">{t('tools.valuesCardSort.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-4xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2"><PrintButton /></div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.valuesCardSort.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.valuesCardSort.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.valuesCardSort.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s6"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.valuesCardSort.round1.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.valuesCardSort.round1.prompt')}</p>

          <div className="mt-s4 grid grid-cols-1 md:grid-cols-2 gap-s2" data-testid="values-pool">
            {valuesPool.map((value) => (
              <CardRow
                key={value}
                value={value}
                bucket={form.buckets[value] ?? null}
                onChange={(b) => setBucket(value, b)}
              />
            ))}
          </div>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.valuesCardSort.round2.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.valuesCardSort.round2.prompt')}</p>

          {starred.length === 0 ? (
            <p className="mt-s3 text-flame-ember text-sm" data-testid="values-round2-empty">
              {t('tools.valuesCardSort.round2.needsRound1')}
            </p>
          ) : (
            <>
              <div className="mt-s4 flex flex-wrap gap-s2" role="group" data-testid="values-pick-group">
                {starred.map((value) => {
                  const active = form.topFive.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleTopFive(value)}
                      data-testid={`values-pick-${value}`}
                      className={
                        active
                          ? 'px-s3 py-s2 rounded-md bg-flame text-bone text-sm font-medium'
                          : 'px-s3 py-s2 rounded-md border border-bone-3 text-gray-2 text-sm hover:text-ink'
                      }
                      aria-pressed={active}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>

              {form.topFive.length > 0 && (
                <ol className="mt-s4 space-y-s2" data-testid="values-top-five">
                  {form.topFive.map((value, idx) => (
                    <li
                      key={value}
                      data-testid={`values-rank-${idx + 1}`}
                      className="flex items-center gap-s3 rounded-md border border-bone-3 bg-bone p-s2"
                    >
                      <span className="font-mono text-xs text-gray-1 w-6">{idx + 1}</span>
                      <span className="flex-1 text-sm text-ink">{value}</span>
                      <button
                        type="button"
                        onClick={() => moveTopFive(idx, -1)}
                        disabled={idx === 0}
                        className="text-xs text-gray-1 hover:text-ink disabled:opacity-40"
                        data-testid={`values-rank-up-${idx}`}
                        aria-label="up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveTopFive(idx, 1)}
                        disabled={idx === form.topFive.length - 1}
                        className="text-xs text-gray-1 hover:text-ink disabled:opacity-40"
                        data-testid={`values-rank-down-${idx}`}
                        aria-label="down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleTopFive(value)}
                        className="text-xs text-gray-1 hover:text-flame-ember"
                        data-testid={`values-rank-remove-${idx}`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label htmlFor="values-reflection" className="block">
            <h2 className="font-display text-display-sm text-ink">
              {t('tools.valuesCardSort.reflection.title')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.valuesCardSort.optional')}
              </span>
            </h2>
            <p className="mt-s2 text-gray-2 italic">
              {t('tools.valuesCardSort.reflection.prompt')}
            </p>
          </label>
          <textarea
            id="values-reflection"
            value={form.reflection}
            onChange={(event) => setForm((prev) => ({ ...prev, reflection: event.target.value }))}
            placeholder={t('tools.valuesCardSort.reflection.placeholder')}
            className="input-field mt-s3 min-h-24"
            data-testid="values-reflection"
            rows={4}
          />
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="values-card-sort-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="values-card-sort-saved-at"
          >
            ✓ {t('tools.valuesCardSort.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.valuesCardSort.submitting') : t('tools.valuesCardSort.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}

interface CardRowProps {
  value: string;
  bucket: Bucket | null;
  onChange: (bucket: Bucket) => void;
}

function CardRow({ value, bucket, onChange }: CardRowProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-s2 rounded-md border border-bone-3 bg-bone p-s2"
      data-testid={`values-card-${value}`}
    >
      <span className="flex-1 text-sm text-ink truncate">{value}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={value}>
        {BUCKETS.map((b) => {
          const active = bucket === b;
          return (
            <button
              key={b}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(b)}
              data-testid={`values-bucket-${value}-${b}`}
              className={
                active
                  ? 'h-7 px-s2 rounded text-xs bg-flame text-bone font-medium'
                  : 'h-7 px-s2 rounded text-xs border border-bone-3 text-gray-1 hover:text-ink'
              }
              title={t(`tools.valuesCardSort.buckets.${b}`)}
            >
              {bucketShortLabel(b)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function bucketShortLabel(b: Bucket): string {
  switch (b) {
    case 'star':
      return '★';
    case 'check':
      return '✓';
  }
}
